import { Injectable, inject } from '@angular/core'

import type { Observable } from 'rxjs'
import { filter, map } from 'rxjs'

import { ApiService } from '../../api/api.service'
import type {
  ProductDto,
  ProductOptionsDto,
  SubscriptionProductOptionDto,
} from '../../api/responses'
import type { ProductOptionAmount } from '../../features/products/models/order-line'

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private readonly api = inject(ApiService)

  getProducts(): Observable<ProductDto[]> {
    return this.api.getProducts().pipe(
      map(result => result.data?.Products?.docs),
      map(products => {
        return products.map(p => ({
          ...p,
        }))
      }),
      filter(Boolean)
    )
  }

  getProductById(productId: number): Observable<ProductDto | undefined> {
    return this.getProducts().pipe(
      map(products => products.find(p => p.id === productId))
    )
  }

  getPrice(
    product_options: ProductOptionsDto[] | SubscriptionProductOptionDto[],
    seats: number
  ): number {
    if (product_options.length === 0) return 0
    const options = this.getProductOptionsForSeats(product_options, seats)

    return options.reduce(
      (total, option) =>
        total + (option.productOption.invoicing?.price ?? 0) * option.quantity,
      0
    )
  }

  // Calculate price including VAT
  getPriceVat(
    product_options: ProductOptionsDto[] | SubscriptionProductOptionDto[],
    seats: number
  ): number {
    // Return 0 if there are no product options
    if (product_options.length === 0) return 0

    // Get the product options applicable for the given number of seats
    const options = this.getProductOptionsForSeats(product_options, seats)

    // Calculate the total price including VAT
    return options.reduce((total, option) => {
      // Get the base price, defaulting to 0 if not specified
      const price = option.productOption.invoicing.price ?? 0
      // Get the tax rate, defaulting to 0 if not specified
      const taxRate = option.productOption.invoicing.tax_rate ?? 0
      // Calculate the tax multiplier
      const taxMultiplier = 1 + taxRate / 100

      // Sum up the total price with tax for each option
      return total + price * taxMultiplier * option.quantity
    }, 0)
  }

  // Calculate the product options for a given number of seats
  getProductOptionsForSeats(
    product_options: ProductOptionsDto[] | SubscriptionProductOptionDto[],
    seats = 0
  ): ProductOptionAmount[] {
    if (seats === 0) return []

    const productOptions: ProductOptionAmount[] = []

    // Product options that refer to bases in pricing tieres (t/m 40, t/m 50, etc.)
    const baseProducts = product_options
      .filter(option => option.from_seats === 0 && (option.seat_value ?? 0) > 1)
      .sort((a, b) => (a.seat_value ?? 0) - (b.seat_value ?? 0))

    // Product options that refer to seats in pricing tieres (41 t/m 50, 51 t/m 60, etc.)
    const seatProducts = product_options
      .filter(option => option.seat_value === 1 || option.seat_value === null)
      .sort((a, b) => a.from_seats - b.from_seats)

    // Only apply base product if first seat product doesnt start from 1 or amount of seats is above the first base product amount
    const needsBaseProduct =
      baseProducts.length > 0 &&
      (seatProducts[0]?.from_seats > 1 ||
        seats >= (baseProducts[0]?.seat_value ?? 0))

    // Get the first base price that is valid for the amounts of seats
    const baseProduct =
      baseProducts.length && needsBaseProduct
        ? baseProducts.reduce((a, b) => ((b.seat_value ?? 0) <= seats ? b : a))
        : undefined

    if (baseProduct) {
      productOptions.push({
        productOption: baseProduct,
        quantity: 1,
      })
    }

    // Get the first seat product that is valid for the amount of seats
    const seatProduct =
      seatProducts.length > 0
        ? seatProducts.reduce((a, b) => (b.from_seats <= seats ? b : a))
        : undefined

    if (seatProduct) {
      productOptions.push({
        productOption: seatProduct,
        quantity: Math.max(seats - (baseProduct?.seat_value ?? 0), 0),
      })
    }

    return productOptions
  }
}
